/* global modForm modUtils Modalytics */
/**
 * HS Endpoints:
 * Dev: https://hsapiservice.dev.quinstreet.net/calls/matches
 * Stage: https://hsapiservice.quinstage.com/calls/matches
 * Prod: https://form-service-hs.qnst.com/calls/matches
 */
/**
 * This function gets the phone assignment data from the quest api.
 * @param {object} trafficDetails - Accepts response object of `TrafficDetails` api.
 * @param {boolean} shouldUpdateExistingPhoneAssignment - Pass `true` if this function is being called multiple times
 * @returns {void}
 */
let abortController = null;
export const getPhoneAssignmentData = function(trafficDetails, shouldUpdateExistingPhoneAssignment = false) {
	if (abortController) {
		return;
	}
	const currentParams = modUtils.getUrlParamsToObject();
	const { CCID, CLK, QTR } = trafficDetails;
	const { phoneNumberLinksSelector, phoneNumbersTextSelector, matchVendors, formatNumberWithBracketsAndDash, skipMatching, getPhoneAssignmentDataCallback, customPhoneNumber, additionalParams } = modForm.opts.phoneAssignmentConfig;
	const { zip } = currentParams;
	const isProductionEnv = modUtils.getEnv() === 'production';
	const isLocalEnv = modUtils.getEnv() === 'local';
	const phoneNumberRegex = new RegExp(/(\d{3})(\d{3})(\d{4})/g);
	const phoneNumberLinks = document.querySelectorAll(phoneNumberLinksSelector);
	let url = '';
	let payload = {};
	let requestBody = payload;
	let apiResponse = {};

	const apiDomain = `https://${isProductionEnv ? 'form-service-hs.qnst.com' : 'hsapiservice.quinstage.com'}`;
	const requestHeaders = new Headers();
	requestHeaders.append('Accept', 'application/json');

	const hidePhoneNumberContainers = () => {
		window.fetchedPhoneNumber = null;
		document.body.classList.remove('call-tracking');
		Modalytics.heap('track', ['Phone Assignment', {
			phoneNumber: false,
			updatedExistingPhoneAssignment: shouldUpdateExistingPhoneAssignment
		}]);
	};

	const showPhoneNumber = (phoneNumber, extension, phoneAssignmentKey) => {
		window.fetchedPhoneNumber = `${phoneNumber}${extension ? `,${extension}` : ''}`;
		const shouldDisplayPhoneNumber = (phoneNumberLinks && phoneNumberLinks.length) && phoneNumber;

		if (shouldDisplayPhoneNumber) {
			document.body.classList.add('call-tracking');
			const formattedPhoneNumber = phoneNumber.replace(phoneNumberRegex, `${formatNumberWithBracketsAndDash ? '($1) $2-$3' : '$1-$2-$3'}`);
			const formattedPhoneNumberWithExtension = `${formattedPhoneNumber}${extension ? ` <span class="call-extension">Ext: ${extension}</span>` : ''}`;
			modForm.opts.phoneAssignmentConfig.response = {
				displayNumber: formattedPhoneNumberWithExtension,
				hrefPhoneNumber: `${phoneNumber}${extension ? `,${extension}` : ''}`,
				extension,
				phoneNumber,
				formattedPhoneNumber,
				phoneAssignmentKey
			};
			phoneNumberLinks.forEach((phoneNumberLink) => {
				phoneNumberLink.setAttribute('href', `tel:${phoneNumber}${extension ? `,${extension}` : ''}`);
				const phoneNumberTextNode = phoneNumberLink.querySelector(phoneNumbersTextSelector);
				if (phoneNumberTextNode) {
					phoneNumberTextNode.innerHTML = formattedPhoneNumberWithExtension;
				}
			});

			Modalytics.heap('track', ['Phone Assignment', {
				redshift_phoneAssignment: phoneNumber, // eslint-disable-line camelcase
				phoneNumber: true,
				extension: extension ? true : false,
				updatedExistingPhoneAssignment: shouldUpdateExistingPhoneAssignment
			}]);
		}
	};

	const zipCode = modForm.opts?.zipCode || null;
	let zipForLocalizedAssignment = null;

	if (!zipCode) {
		zipForLocalizedAssignment = !modForm.opts.usedZipFromParams && zip ? (modForm.opts.usedZipFromParams = true, zip) : window.geolocation?.currentLocation?.zipcode || null;
	}

	requestHeaders.append('Content-Type', 'application/json');
	url = `${apiDomain}/calls/matches`;

	payload = {
		ccid: CCID || null,
		clk: CLK || null,
		qtr: QTR || null,
		landingPageURL: window.location.href,
		matchVendors: (Array.isArray(matchVendors) && matchVendors.length) ? matchVendors : [-1],
		service: modForm.opts.vertical,
		site: window.location.host + (window.location.pathname !== '/' ? window.location.pathname : ''),
		skipMatching: skipMatching,
		zip: zipCode,
		zipForLocalizedAssignment
	};

	if (!payload.zip && isLocalEnv) {
		payload.zip = '94404';
	}

	if (additionalParams && Object.keys(additionalParams).length) {
		Object.keys(additionalParams).forEach((key) => {
			payload[key] = additionalParams[key];
		});
	}

	if (customPhoneNumber) {
		payload.assignPhoneNumber = false;
	}

	// If active experiment is loaded and there's an active campaign
	if (window.activeCampaignData) {
		payload.trackingInfo = {
			'TestID': window.activeCampaignData['Test ID'],
			'VariationName': window.activeCampaignData['Variation Name']
		};
	}

	if (shouldUpdateExistingPhoneAssignment && modForm.opts.phoneAssignmentConfig.response && modForm.opts.phoneAssignmentConfig.response.phoneAssignmentKey) {
		payload.vendorPhoneAssignmentKeys = {
			[payload.matchVendors[0]]: modForm.opts.phoneAssignmentConfig.response.phoneAssignmentKey
		};
	}

	requestBody = JSON.stringify(payload);

	abortController = new AbortController();
	const signal = abortController.signal;

	const requestOptions = {
		method: 'POST',
		headers: requestHeaders,
		redirect: 'follow',
		mode: 'cors',
		body: requestBody,
		signal
	};

	fetch(url, requestOptions)
		.then(response => response.json())
		.then(jsonResponse => {
			apiResponse = jsonResponse;
			if (jsonResponse && jsonResponse.status === 'success') {
				const { data } = jsonResponse;
				const { phoneDetails, matches } = data;
				let phoneNumberData;
				if (!phoneDetails) {
					phoneNumberData = modForm.opts.phoneAssignmentConfig.response;
					if (customPhoneNumber && matches && matches.length) {
						phoneNumberData = {
							phoneNumber: customPhoneNumber
						};
					}
				} else {
					phoneNumberData = phoneDetails[payload.matchVendors[0]];
				}

				if (phoneNumberData) {
					const { phoneNumber, extension, phoneAssignmentKey } = phoneNumberData;
					showPhoneNumber(phoneNumber, extension, phoneAssignmentKey);
				}
			} else {
				hidePhoneNumberContainers();
			}
		})
		.catch(error => {
			if (signal.aborted) {
				console.log(signal.reason);
				Modalytics.heap('track', ['Phone Assignment', {
					isRequestAborted: true
				}]);
			} else {
				hidePhoneNumberContainers();
				console.log('Something went wrong!', error);
			}
		}).finally(() => {
			if (getPhoneAssignmentDataCallback) {
				getPhoneAssignmentDataCallback(apiResponse);
			}
			abortController = null;
		});
};
